/**
 * Cross-domain staff endpoints the technician app borrows for the
 * permission-gated read surfaces (inventory, service catalog). These live
 * under `/admin/*` server-side; technicians reach the read subset because
 * their role bundle grants the matching scoped permission (CATALOG_READ —
 * see backend RolePermissionMapping). They do NOT hold the full *_MANAGE
 * bundles, so writes still 403. The backend is authoritative on 403.
 *
 * Endpoint contracts (verified against the live controllers):
 *   GET    /admin/catalog/parts?page&size            → Page<PartResponse>
 *   GET    /admin/catalog/services?page&size          → Page<ServiceTypeResponse>
 *   GET    /technical/stock/{partId}                  → StockLevelResponse (404 if uninit)
 *   GET    /technical/notifications                    → Page<NotificationInboxResponse>
 *
 * Pure endpoint wrappers — no React. Hooks layer on top in useStaff.ts.
 */
import { ApiError, api } from './apiClient'
import type {
  CatalogPartResponse,
  CreatePartRequestBody,
  PartRequestResponse,
  NotificationResponse,
  ServiceTypeResponse,
  SpringPage,
  StockLevelResponse,
} from './types'

const enc = encodeURIComponent

export interface CreateVehicleBody {
  readonly licensePlate: string
  readonly make?: string | null
  readonly model?: string | null
  readonly yearOfManufacture?: number | null
  readonly vin?: string | null
  readonly color?: string | null
  readonly firstRegisteredAt?: string | null
}

/** One line of a material-reorder request. */
export interface ReorderItemBody {
  /** Catalog part id, or null for a brand-new material typed by the mechanic. */
  readonly partId: string | null
  readonly name: string
  readonly quantity: number
  readonly note: string | null
}

export interface ReorderRequestBody {
  readonly items: ReadonlyArray<ReorderItemBody>
}

export const staffApi = {
  // --- Inventory ---------------------------------------------------------
  // size 500: the price lists render single-page — a 100-row default would
  // silently truncate a grown catalog.
  listParts: (page = 0, size = 500) =>
    api.get<SpringPage<CatalogPartResponse>>(
      `/admin/catalog/parts?page=${page}&size=${size}`,
    ),

  /** Stock for one part. Returns null on 404 (part never stocked) so the
   *  inventory list can show "—" rather than throwing the whole page. */
  getStock: async (partId: string): Promise<StockLevelResponse | null> => {
    try {
      return await api.get<StockLevelResponse>(`/technical/stock/${enc(partId)}`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null
      throw e
    }
  },

  /** Report used-up / missing materials to management for reordering. Each
   *  line is one inventory-kind part request (POST /technical/part-requests,
   *  TechnicalPartRequestController) — the endpoint takes a single item, so a
   *  multi-line reorder fans out to one request per line. New materials
   *  (partId=null) are recorded by label for management to reconcile. */
  submitReorder: async (body: ReorderRequestBody): Promise<void> => {
    await Promise.all(
      body.items.map((it) =>
        api.post<unknown>('/technical/part-requests', {
          kind: 'INVENTORY',
          workOrderId: null,
          partId: it.partId,
          partLabel: it.name,
          quantity: it.quantity,
          note: it.note,
        }),
      ),
    )
  },

  /** File a single part request — the only way a technician names a WO part.
   *  It goes to management to accept; there is no direct-write path any
   *  more. */
  createPartRequest: (body: CreatePartRequestBody) =>
    api.post<PartRequestResponse>('/technical/part-requests', body),

  /** Every part request on a work order, any status — the workspace needs
   *  the decided ones too, so a tech sees the answer and not just silence. */
  listPartRequests: (workOrderId: string) =>
    api.get<PartRequestResponse[]>(
      `/technical/part-requests?workOrderId=${encodeURIComponent(workOrderId)}`,
    ),

  /** Take back one's own still-pending request. */
  withdrawPartRequest: (id: string) =>
    api.post<PartRequestResponse>(
      `/technical/part-requests/${encodeURIComponent(id)}/withdraw`,
      {},
    ),

  // --- Catalog (services picker + the Services price list) ---------------
  listServices: (page = 0, size = 500) =>
    api.get<SpringPage<ServiceTypeResponse>>(
      `/admin/catalog/services?page=${page}&size=${size}`,
    ),

  // --- Notifications (technician-scoped inbox; read-only) ----------------
  /** Defensive: the inbox may return a bare array or a Spring page. We
   *  normalise to an array and tolerate either. (The old /admin/notifications
   *  path now 403s for technicians.) */
  listNotifications: async (): Promise<ReadonlyArray<NotificationResponse>> => {
    const raw = await api.get<
      ReadonlyArray<NotificationResponse> | SpringPage<NotificationResponse>
    >('/technical/notifications')
    // `Array.isArray` doesn't narrow a readonly array out of the union, so
    // discriminate on the page envelope's `content` field instead.
    return 'content' in raw ? raw.content : raw
  },

  /**
   * Dismiss one notification. Only entries assigned to the calling
   * technician can be dismissed — the bell also lists unassigned
   * shop-wide rows, and the backend 403s those on purpose so one
   * technician cannot clear something the rest of the shop still needs.
   */
  dismissNotification: (id: string) =>
    api.post<NotificationResponse>(`/technical/notifications/${enc(id)}/dismiss`, {}),

  // Deliberately no `markNotificationRead` wrapper for POST
  // /technical/notifications/{id}/read, even though the backend endpoint
  // exists (TechnicalNotificationsController#markRead). Investigated
  // 2026-08-14: `GET /technical/notifications` (above) already IS the
  // "unread" query server-side — NotificationInboxService
  // .listUnreadForTechnician -> findUnreadAssignedOrInCategories filters
  // `readAt IS NULL AND resolvedAt IS NULL` — so every row this screen
  // ever renders already has `readAt === null`. There is no "read but
  // still open" state visible here to react to, unlike the management
  // inbox: `NotificationInboxEntry.markResolved` already stamps `readAt`
  // when it's null, so `dismissNotification` (-> resolveOwn ->
  // markResolved) already clears both flags together and the row drops
  // out of the list — the badge (Topbar.tsx's `hasUnread`) already goes
  // quiet for free. Wiring a bare "mark read" call here (e.g. on bell
  // open) would not clear anything the badge doesn't already clear on
  // dismiss; it would instead flip `readAt` on a row that's still
  // unresolved, and the very next 30s poll would silently drop it from
  // the technician's own list without them dismissing it — the same
  // "vanished while still open" bug the management inbox was just fixed
  // for (see notificationsApi.ts). Fixing that safely needs a
  // technician-scoped "unresolved regardless of read" backend query
  // (mirroring `status=open` on the manager side), which doesn't exist
  // today — that's a backend change, not a frontend wiring job.

  /** Dismiss every notification assigned to this technician. One request. */
  dismissAllNotifications: () =>
    api.post<void>('/technical/notifications/dismiss-all', {}),
}

/** Catalog part label honouring the active locale. */
export function partLabel(p: CatalogPartResponse, lang: 'de' | 'en'): string {
  return (lang === 'de' ? p.labelDeAT : p.labelEn) || p.sku
}

/**
 * Catalog service label. Single `displayName` field now (V0228
 * amendment) — whatever the manager typed, in whichever language they
 * chose; no lang branch left to pick between. Falls back to the key on
 * the pathological empty-name case.
 */
export function serviceLabel(s: ServiceTypeResponse): string {
  return s.displayName || s.key
}

/**
 * Self-service password change — `POST /users/me/password`
 * ({@code AuthController.changePassword}).
 *
 * Not under `/technical/*` and not role-gated: the endpoint resolves the
 * caller from the session, so a technician reaches it exactly as an admin
 * does. Distinct from `/admin/users/{id}/password`, which sets someone
 * else's and does NOT re-verify a current one.
 *
 * 204 on success. A wrong current password is a 400, deliberately not a
 * 401 — the shared client reads 401 as an expired session and would
 * bounce the tech to sign-in mid-form.
 */
export function changeOwnPassword(body: {
  readonly currentPassword: string
  readonly newPassword: string
}): Promise<void> {
  return api.post<void>('/users/me/password', body)
}

/**
 * The wrong-current-password signal, as it actually arrives.
 *
 * `AuthController` raises a bare `ResponseStatusException`, so the problem
 * `type` is the generic `urn:vollgas:problem:bad-request` and
 * `ApiError.code` cannot be matched on — the reason survives only in
 * `detail`, i.e. `ApiError.message`.
 */
export const WRONG_CURRENT_PASSWORD_DETAIL = 'current-password-incorrect'

/** Mirrors `ChangePasswordRequest`'s `@Size(min = 8)`. No max server-side. */
export const PASSWORD_MIN_LENGTH = 8
